import httpClient from './httpClient';

interface SubmitJDPayload {
  jobDescription: string;
  name?: string;
  deadline?: string;
  interviewId?: string;
  customisationLevel?: 'low' | 'medium' | 'high';
}

export interface SmartBuildJDPayload {
  smartBuild: true;
  roleId: string;
  responsibilityLevel?: number;
  deadline?: string;
}

export type ProcessJDPayload = SubmitJDPayload | SmartBuildJDPayload;

export interface JDExtractedData {
  company_name: string;
  domain_name: string;
  role_name: string;
  skills: string[];
  responsibilities: string[];
  qualifications: string[];
  additional_info: Record<string, string>;
  confidence_score: number;
}

export interface SuggestedPipelineRound {
  order: number;
  name: string;
  interviewType: string;
  label: string;
  scheduledDate?: string;
  isConfigured: boolean;
  config: Record<string, unknown>;
}

export interface SuggestedPipeline {
  name: string;
  deadline?: string;
  startsAt?: string;
  rounds: SuggestedPipelineRound[];
}

export interface JDStatusResponse {
  interviewId: string;
  jdProcessingStatus: 'queued' | 'processing' | 'processed' | 'violation' | 'failed' | 'unknown';
  interviewStatus?: 'draft' | 'published' | 'archived';
  jobDescription?: string;
  extractedData?: JDExtractedData;
  graphId?: string;
  violations?: string[];
  suggestedPipeline?: SuggestedPipeline;
}

export interface ScheduleInterviewResponse {
  interviewId: string;
  interviewStatus: 'published';
  jdProcessingStatus: string | null;
  scheduledAt: string | null;
}

export interface CustomInterviewListItem {
  id: string;
  ownerType: string;
  name: string;
  jobDescription: string;
  status: string;
  jdProcessingStatus: string | null;
  jdExtractedData: JDExtractedData | null;
  graphId: string | null;
  createdAt: string;
  updatedAt: string;
  [key: string]: unknown;
}

export const submitJobDescription = (data: SubmitJDPayload) =>
  httpClient.post<{ interviewId: string; status: string }>('orchestrator/jd/process', data);

export const submitSmartBuild = (data: SmartBuildJDPayload) =>
  httpClient.post<{ interviewId: string; status: string }>('orchestrator/jd/process', data);

export const pollJDStatus = (interviewId: string) =>
  httpClient.get<JDStatusResponse>(`orchestrator/jd/status/${interviewId}`);

export interface BackendInterviewRound {
  id?: string;
  type: 'aptitude' | 'coding' | 'gd';
  scheduledDate: string;
  isConfigured: boolean;
  config: Record<string, unknown>;
}

export interface UpdateCustomInterviewPayload {
  name?: string;
  deadline?: string;
  jobDescription?: string;
  rounds?: BackendInterviewRound[];
}

export const updateCustomInterview = (interviewId: string, data: UpdateCustomInterviewPayload) =>
  httpClient.put<CustomInterviewListItem>(`orchestrator/custom-interviews/${interviewId}`, data);

export const scheduleCustomInterview = (interviewId: string) =>
  httpClient.post<ScheduleInterviewResponse>(
    `orchestrator/custom-interviews/${interviewId}/schedule`,
  );

export const fetchUserCustomInterviews = () =>
  httpClient.get<{ interviews: CustomInterviewListItem[]; count: number }>('orchestrator/custom-interviews');

export const deleteCustomInterview = (interviewId: string) =>
  httpClient.delete<{ message: string; id: string }>(`orchestrator/custom-interviews/${interviewId}`);

export interface CustomisedResumeResponse {
  interviewId: string;
  status: 'queued' | 'processing' | 'completed' | 'failed';
  resumeMarkdown: string | null;
}

export const fetchCustomisedResume = (interviewId: string) =>
  httpClient.get<CustomisedResumeResponse>(
    `orchestrator/resume/customised/${interviewId}`
  );

export const customiseResume = (interviewId: string, customisationLevel: 'low' | 'medium' | 'high') =>
  httpClient.post<{ interviewId: string; status: string }>(
    `orchestrator/custom-interviews/${interviewId}/customise-resume`,
    { customisationLevel }
  );

export interface ResumeCreditsResponse {
  credits: number;
  enrolled: boolean;
}

export const fetchResumeCredits = () =>
  httpClient.get<ResumeCreditsResponse>('orchestrator/resume/credits');

export interface SavedResumeFromDB {
  id: string;
  userId: string;
  interviewId: string;
  resumeMarkdown: string;
  buildStatus: string;
  createdAt: string;
  updatedAt: string;
  interviewName: string;
  roleName?: string;
  skills: string[];
}

export const fetchAllCustomisedResumes = () =>
  httpClient.get<{ resumes: SavedResumeFromDB[] }>('orchestrator/resume/customised');
